import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DomainStatus, TenantDomain } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Badge, Button, EmptyRow, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';

/** GET /platform/domains (core-phase4 s5): a tenant domain plus its owner. Not in the shared package yet. */
export const PlatformDomain = TenantDomain.extend({ tenantId: z.uuid(), tenantName: z.string() });
export type PlatformDomain = z.infer<typeof PlatformDomain>;
const PlatformDomainList = z.array(PlatformDomain);

const PAGE_SIZE = 25;
const TONE = { verified: 'good', pending_dns: 'warn', failed: 'bad', disabled: 'neutral' } as const;

/** Every custom domain across customers, with search, status filter, sort, paging and disable. */
export function PlatformDomainsPage() {
  const qc = useQueryClient();
  const canManage = useCan('platform.tenants_manage');
  const list = useListState({ sort: 'hostname' });
  const [status, setStatus] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['platform', 'domains'], queryFn: () => api.get('/platform/domains', { schema: PlatformDomainList }) });
  const disable = useMutation({
    mutationFn: (d: PlatformDomain) => api.post(`/platform/domains/${d.id}/disable`),
    onSuccess: async (_r, d) => {
      setNotice(t('partner.domains.disabled', { hostname: d.hostname }));
      await qc.invalidateQueries({ queryKey: ['platform', 'domains'] });
    },
    onError: () => setNotice(null),
  });

  const rows = useMemo(() => {
    const needle = list.search.trim().toLowerCase();
    const filtered = (q.data ?? []).filter(
      (d) => (!status || d.status === status) && (!needle || d.hostname.toLowerCase().includes(needle) || d.tenantName.toLowerCase().includes(needle)),
    );
    const key = list.sort === 'tenantName' || list.sort === 'status' ? list.sort : 'hostname';
    const dir = list.order === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => a[key].localeCompare(b[key]) * dir);
  }, [q.data, list.search, list.sort, list.order, status]);
  const pageRows = rows.slice((list.page - 1) * PAGE_SIZE, list.page * PAGE_SIZE);

  return (
    <>
      <PageHeader title={t('partner.domains.title')} description={t('partner.domains.description')} />
      <div className="space-y-4">
        <FilterBar>
          <TextField label={t('common.search')} type="search" placeholder={t('partner.domains.search')} value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
          <SelectField
            label={t('partner.domains.filterStatus')}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              list.resetPage();
            }}
          >
            <option value="">{t('partner.domains.any')}</option>
            {DomainStatus.options.map((s) => (
              <option key={s} value={s}>
                {t(`partner.domains.status.${s}`)}
              </option>
            ))}
          </SelectField>
        </FilterBar>
        {notice ? <Alert kind="success">{notice}</Alert> : null}
        <ErrorLine error={disable.error} />
        {q.isPending ? <Spinner /> : null}
        {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
        {q.data ? (
          <>
            <TableWrap caption={t('partner.domains.title')}>
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <SortHeader label={t('partner.domains.hostname')} field="hostname" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                  <SortHeader label={t('partner.domains.customer')} field="tenantName" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                  <Th>{t('partner.domains.kind')}</Th>
                  <SortHeader label={t('common.status')} field="status" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                  {canManage ? <Th>{t('common.actions')}</Th> : null}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? <EmptyRow cols={canManage ? 5 : 4} /> : null}
                {pageRows.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono">
                      {d.hostname} {d.primary ? <Badge>{t('partner.domains.primary')}</Badge> : null}
                    </td>
                    <td className="px-4 py-3">
                      <Link className="text-brand hover:underline" to={`/platform/tenants/${d.tenantId}`}>
                        {d.tenantName}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{t(`partner.domains.kind.${d.kind}`)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={TONE[d.status]}>{t(`partner.domains.status.${d.status}`)}</Badge>
                      {d.verifiedAt ? <span className="ml-2 text-xs text-slate-600">{formatDateTime(d.verifiedAt)}</span> : null}
                    </td>
                    {canManage ? (
                      <td className="px-4 py-3">
                        {d.status !== 'disabled' ? (
                          <Button
                            variant="danger"
                            disabled={disable.isPending}
                            aria-label={t('partner.domains.disableFor', { hostname: d.hostname })}
                            onClick={() => disable.mutate(d)}
                          >
                            {t('partner.domains.disable')}
                          </Button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <Pager page={list.page} pageSize={PAGE_SIZE} total={rows.length} onPage={list.setPage} />
          </>
        ) : null}
      </div>
    </>
  );
}
